import gql from 'graphql-tag'
import { FileUploadGraphqlClient, GraphqlClient } from './GraphqlClient'
import {
  Staff,
  DateTimeInput,
  BookingInput,
  ProgramOfficeStatus,
  PublicationDateInput,
  Event,
  LevelOneApprover
} from './Types'

export const createEvent = async (
  title: string,
  summary: string,
  description: string,
  eventType: string,
  isAdminEvent?: boolean
) => {
  const client = GraphqlClient()
  const EVENTINFO = gql`
    mutation (
      $title: String!
      $summary: String!
      $description: String!
      $eventType: String!
      $isAdminEvent: Boolean
    ) {
      createEvent(
        title: $title
        summary: $summary
        description: $description
        eventType: $eventType
        isAdminEvent: $isAdminEvent
      ) {
        data {
          id
          title
          summary
          description
          eventType
        }
        statusCode
        message
      }
    }
  `
  const results = await client.mutate({
    mutation: EVENTINFO,
    variables: {
      title: title,
      summary: summary,
      description: description,
      eventType,
      isAdminEvent
    }
  })
  return results
}

export const updateFullEvent = async (event: Event) => {
  const client = GraphqlClient()
  const EVENTINFO = gql`
    mutation ($eventData: EventInput!) {
      updateFullEvent(eventData: $eventData) {
        data {
          id
          title
          summary
          description
          overHundredParticipants
          sendInvitation
          invitationDistributionList
          invitationNotes
          programOfficeId
          levelOneApproverId
          otherApproverName
          otherApproverEmail
          publicOrPrivate
          featuresTranscription
          featuresScript
          logisticNeed
          supportNeeded
          additionalRequest
          tbd_presenters
          tbd_moderators
          tbd_supportstaff
          status
          eventType
          seriesId
          publicationDate
        }
        statusCode
        message
      }
    }
  `
  const results = await client.mutate({
    mutation: EVENTINFO,
    variables: {
      eventData: {
        id: event.id,
        title: event.title,
        summary: event.summary,
        description: event.description,
        overHundredParticipants: event.overHundredParticipants,
        sendInvitation: event.sendInvitation,
        invitationDistributionList: event.invitationDistributionList,
        invitationNotes: event.invitationNotes || '',
        programOfficeId: event.programOffice?.id,
        levelOneApproverId: event.levelOneApprover?.id,
        otherApproverName: event.otherApproverName,
        otherApproverEmail: event.otherApproverEmail,
        publicOrPrivate: event.publicOrPrivate,
        featuresTranscription: event.featuresTranscription || '',
        featuresScript: event.featuresScript || '',
        logisticNeed: event.logisticNeed,
        supportNeeded: event.supportNeeded,
        additionalRequest: event.additionalRequest,
        tbd_presenters: event.tbd_presenters || 0,
        tbd_moderators: event.tbd_moderators || 0,
        tbd_supportstaff: event.tbd_supportstaff || 0,
        status: event.status,
        eventType: event.eventType,
        seriesId: event.seriesId,
        publicationDate: event.publicationDate
      }
    }
  })
  return results
}

export const updateEventInfo = async (
  id: number,
  title: string,
  summary: string,
  description: string
) => {
  const client = GraphqlClient()
  const EVENTINFO = gql`
    mutation (
      $id: Int!
      $title: String!
      $summary: String!
      $description: String!
    ) {
      updateEvent(
        id: $id
        title: $title
        summary: $summary
        description: $description
      ) {
        data {
          id
          title
          summary
          description
        }
        statusCode
        message
      }
    }
  `
  const results = await client.mutate({
    mutation: EVENTINFO,
    variables: {
      id: id,
      title: title,
      summary: summary,
      description: description
    }
  })
  return results
}

export const updateEventtype = async (id: number, eventType: string) => {
  const client = GraphqlClient()
  const EVENTINFO = gql`
    mutation ($id: Int!, $eventType: String!) {
      updateEventtype(id: $id, eventType: $eventType) {
        data {
          id
          eventType
        }
        statusCode
        message
      }
    }
  `
  const results = await client.mutate({
    mutation: EVENTINFO,
    variables: {
      id: id,
      eventType: eventType
    }
  })
  return results
}

export const updatePublicationDate = async (
  id: number,
  input: PublicationDateInput
) => {
  const client = GraphqlClient()
  const EVENTINFO = gql`
    mutation ($id: Int!, $input: PublicationDateInput!) {
      updatePublicationDate(id: $id, input: $input) {
        data {
          id
          eventType
        }
        statusCode
        message
      }
    }
  `
  const results = await client.mutate({
    mutation: EVENTINFO,
    variables: {
      id: id,
      input: input
    }
  })
  return results
}

export const updateAudience = async (
  eventId: number,
  overHundredParticipants: boolean,
  sendInvitation: string,
  invitationDistributionList: string,
  invitationNotes: string
) => {
  const client = GraphqlClient()

  const EVENTINFO = gql`
    mutation (
      $id: Int!
      $overHundredParticipants: Boolean
      $sendInvitation: String
      $invitationDistributionList: String
      $invitationNotes: String
    ) {
      updateAudience(
        id: $id
        overHundredParticipants: $overHundredParticipants
        sendInvitation: $sendInvitation
        invitationDistributionList: $invitationDistributionList
        invitationNotes: $invitationNotes
      ) {
        data {
          sendInvitation
          invitationDistributionList
          invitationNotes
        }
        statusCode
        message
      }
    }
  `
  const results = await client.mutate({
    mutation: EVENTINFO,
    variables: {
      id: eventId,
      overHundredParticipants: overHundredParticipants,
      sendInvitation: sendInvitation,
      invitationDistributionList: invitationDistributionList,
      invitationNotes: invitationNotes
    }
  })

  return results
}

export const updateParticipants = async (
  eventId: number,
  overHundredParticipants: boolean
) => {
  const client = GraphqlClient()

  const EVENTINFO = gql`
    mutation ($id: Int!, $overHundredParticipants: Boolean) {
      updateParticipants(
        id: $id
        overHundredParticipants: $overHundredParticipants
      ) {
        data {
          sendInvitation
          invitationDistributionList
          invitationNotes
        }
        statusCode
        message
      }
    }
  `
  const results = await client.mutate({
    mutation: EVENTINFO,
    variables: { id: eventId, overHundredParticipants: overHundredParticipants }
  })

  return results
}

export const updateTBD = async (
  eventId: number,
  tbd_presenters: number,
  tbd_moderators: number,
  tbd_supportstaff: number
) => {
  const client = GraphqlClient()

  const EVENTINFO = gql`
    mutation (
      $id: Int!
      $tbd_presenters: Int
      $tbd_moderators: Int
      $tbd_supportstaff: Int
    ) {
      updateTBD(
        id: $id
        tbd_presenters: $tbd_presenters
        tbd_moderators: $tbd_moderators
        tbd_supportstaff: $tbd_supportstaff
      ) {
        data {
          tbd_moderators
          tbd_presenters
          tbd_supportstaff
          invitationNotes
        }
        statusCode
        message
      }
    }
  `
  const results = await client.mutate({
    mutation: EVENTINFO,
    variables: {
      id: eventId,
      tbd_presenters: tbd_presenters,
      tbd_moderators: tbd_moderators,
      tbd_supportstaff: tbd_supportstaff
    }
  })

  return results
}

export const updateFeature = async (
  eventId: number,
  programOfficeId: number,
  levelOneApproverId: number,
  otherApproverName: string,
  otherApproverEmail: string,
  publicOrPrivate: string,
  logisticNeed: string,
  supportNeeded: string,
  additionalRequest: string
) => {
  const client = GraphqlClient()

  const EVENTINFO = gql`
    mutation (
      $id: Int!
      $programOfficeId: Int
      $levelOneApproverId: Int
      $otherApproverName: String
      $otherApproverEmail: String
      $publicOrPrivate: String
      $logisticNeed: String
      $supportNeeded: String
      $additionalRequest: String
    ) {
      updateFeature(
        id: $id
        programOfficeId: $programOfficeId
        levelOneApproverId: $levelOneApproverId
        otherApproverName: $otherApproverName
        otherApproverEmail: $otherApproverEmail
        publicOrPrivate: $publicOrPrivate
        logisticNeed: $logisticNeed
        supportNeeded: $supportNeeded
        additionalRequest: $additionalRequest
      ) {
        data {
          programOfficeId
          levelOneApproverId
          otherApproverName
          otherApproverEmail
          publicOrPrivate
          logisticNeed
          supportNeeded
          additionalRequest
        }
        statusCode
        message
      }
    }
  `
  const results = await client.mutate({
    mutation: EVENTINFO,
    variables: {
      id: eventId,
      programOfficeId,
      levelOneApproverId,
      otherApproverName,
      otherApproverEmail,
      publicOrPrivate,
      logisticNeed,
      supportNeeded,
      additionalRequest
    }
  })
  return results
}

export const updateFeature2 = async (
  eventId: number,
  publicOrPrivate: string,
  featuresTranscription: string,
  featuresScript: string,
  additionalRequest: string
) => {
  const client = GraphqlClient()

  const EVENTINFO = gql`
    mutation (
      $id: Int!
      $publicOrPrivate: String
      $featuresTranscription: String
      $featuresScript: String
      $additionalRequest: String
    ) {
      updateFeature2(
        id: $id
        publicOrPrivate: $publicOrPrivate
        featuresTranscription: $featuresTranscription
        featuresScript: $featuresScript
        additionalRequest: $additionalRequest
      ) {
        data {
          programOfficeId
          levelOneApproverId
          otherApproverName
          otherApproverEmail
          publicOrPrivate
          featuresTranscription
          featuresScript
          logisticNeed
          supportNeeded
          additionalRequest
        }
        statusCode
        message
      }
    }
  `
  const results = await client.mutate({
    mutation: EVENTINFO,
    variables: {
      id: eventId,
      publicOrPrivate,
      featuresTranscription,
      featuresScript,
      additionalRequest
    }
  })
  return results
}

export const updatePOApprover = async (eventId: number, levelOneApproverId: number) => {
  const client = GraphqlClient()

  const mutation = gql`
    mutation ($id: Int!, $levelOneApproverId: Int) {
      updatePOApprover(id: $id, levelOneApproverId: $levelOneApproverId) {
        statusCode
        message
      }
    }
  `

  const results = await client.mutate({
    mutation,
    variables: { id: eventId, levelOneApproverId }
  })

  return results
}

export const createUpdateStaff = async (eventId: number, staff: Staff[]) => {
  const client = GraphqlClient()

  const EVENTINFO = gql`
    mutation ($staff: [StaffInput], $event_id: Int!) {
      createUpdateStaff(staff: $staff, event_id: $event_id) {
        data {
          id
          name
          email
          phone
          programOffice
          jobTitle
          staff_type
        }
        statusCode
        message
      }
    }
  `
  const results = await client.mutate({
    mutation: EVENTINFO,
    variables: { staff: staff, event_id: eventId }
  })

  return results
}

export const getStaff = async (eventId: number) => {
  const client = GraphqlClient()

  const staffQuery = gql`
    query ($event_id: Int!) {
      staff(event_id: $event_id) {
        data {
          id
          name
          email
          phone
          programOffice
          jobTitle
          staff_type
        }
        statusCode
        message
      }
    }
  `
  const results = await client.query({
    query: staffQuery,
    variables: { event_id: eventId }
  })

  return results
}

export const getStaffByProgramOffice = async (programOfficeId: number) => {
  const client = GraphqlClient()

  const staffQuery = gql`
    query ($programOfficeId: Int!) {
      staffByProgramOffice(programOfficeId: $programOfficeId) {
        data {
          id
          name
          email
          phone
          programOffice
          jobTitle
          staff_type
        }
        statusCode
        message
      }
    }
  `
  const results = await client.query({
    query: staffQuery,
    variables: { programOfficeId }
  })

  return results
}

export const bookedSlotsPerDate = async (
  eventId: number,
  input: DateTimeInput,
  eventType?: string
) => {
  const client = GraphqlClient()

  const query = gql`
    query ($eventId: Int!, $input: DateTimeInput!, $eventType: String) {
      bookedSlotsPerDate(eventId: $eventId, input: $input, eventType: $eventType)
    }
  `
  const results = await client.query({
    query,
    variables: { eventId, input, eventType }
  })

  return results
}

export const availableTimeSlots = async (
  eventId: number,
  input: DateTimeInput,
  eventType?: string
) => {
  const client = GraphqlClient()
  const staffQuery = gql`
    query ($eventId: Int!, $input: DateTimeInput!, $eventType: String) {
      availableTimeSlots(eventId: $eventId, input: $input, eventType: $eventType) {
        data
        statusCode
        message
      }
    }
  `
  const results = await client.query({
    query: staffQuery,
    variables: { eventId, input, eventType }
  })
  return results
}

export const selectedSlotRoomId = async (
  selectedSlot: string,
  eventId: number,
  eventType: string,
  input: DateTimeInput,
  adminBooking?: boolean
) => {
  const client = GraphqlClient()
  const staffQuery = gql`
    query ($selectedSlot: String!, $eventId: Int!, $eventType: String!, $input: DateTimeInput!, $adminBooking: Boolean) {
      selectedSlotRoomId(
        selectedSlot: $selectedSlot
        eventId: $eventId
        eventType: $eventType,
        input: $input,
        adminBooking: $adminBooking
      ) {
        data {
          slotRoomId
        }
        statusCode
        message
      }
    }
  `
  const results = await client.query({
    query: staffQuery,
    variables: { selectedSlot, eventId, eventType, input, adminBooking }
  })
  return results
}

export const availableSlotRoomIds = async (
  selectedSlot: string,
  eventId: number,
  eventType: string,
  input: DateTimeInput,
  adminBooking?: boolean
) => {
  const client = GraphqlClient()
  const staffQuery = gql`
    query ($selectedSlot: String!, $eventId: Int!, $eventType: String!, $input: DateTimeInput!, $adminBooking: Boolean) {
      availableSlotRoomIds(
        selectedSlot: $selectedSlot
        eventId: $eventId
        eventType: $eventType,
        input: $input,
        adminBooking: $adminBooking
      ) {
        data
        statusCode
        message
      }
    }
  `
  const results = await client.query({
    query: staffQuery,
    variables: { selectedSlot, eventId, eventType, input, adminBooking }
  })
  return results
}

export const bookTimeSlots = async (
  liveRoomId: number,
  dryRoomId: number,
  eventId: number,
  input: BookingInput
) => {
  const client = GraphqlClient()
  const EVENTINFO = gql`
    mutation (
      $liveRoomId: Int!
      $dryRoomId: Int!
      $eventId: Int!
      $input: BookingInput!
    ) {
      bookTimeSlots(
        liveRoomId: $liveRoomId
        dryRoomId: $dryRoomId
        eventId: $eventId
        input: $input
      ) {
        data
        statusCode
        message
      }
    }
  `
  const results = await client.mutate({
    mutation: EVENTINFO,
    variables: {
      liveRoomId: liveRoomId,
      dryRoomId: dryRoomId,
      eventId: eventId,
      input: input
    }
  })
  return results
}

export const bookMultipleRooms = async (
  eventId: number,
  bookings: Array<{
    roomId: number,
    date: string,
    startTime: number,
    endTime: number,
    category: string,
    type: string
  }>
) => {
  const client = GraphqlClient()
  const EVENTINFO = gql`
    mutation ($input: MultipleRoomBookingInput!) {
      bookMultipleRooms(input: $input) {
        data
        statusCode
        message
      }
    }
  `
  const results = await client.mutate({
    mutation: EVENTINFO,
    variables: {
      input: {
        eventId: eventId,
        bookings: bookings
      }
    }
  })
  return results
}

export const bookTimeSlotsLive = async (
  liveRoomId: number,
  dryRoomId: number,
  eventId: number,
  input: BookingInput
) => {
  const client = GraphqlClient()
  const EVENTINFO = gql`
    mutation (
      $liveRoomId: Int!
      $dryRoomId: Int!
      $eventId: Int!
      $input: BookingInput!
    ) {
      bookTimeSlotsLive(
        liveRoomId: $liveRoomId
        dryRoomId: $dryRoomId
        eventId: $eventId
        input: $input
      ) {
        data
        statusCode
        message
      }
    }
  `
  const results = await client.mutate({
    mutation: EVENTINFO,
    variables: {
      liveRoomId: liveRoomId,
      dryRoomId: dryRoomId,
      eventId: eventId,
      input: input
    }
  })
  return results
}

export const submitEvent = async (eventId: number) => {
  const client = GraphqlClient()

  const EVENTINFO = gql`
    mutation ($id: Int!) {
      submitEvent(id: $id) {
        statusCode
        message
      }
    }
  `
  const results = await client.mutate({
    mutation: EVENTINFO,
    variables: { id: eventId }
  })

  return results
}

export const eventCompleted = async (
  eventId: number,
  programOfficeId: number,
  levelOneApproverId: number
) => {
  const client = GraphqlClient()

  const EVENTINFO = gql`
    mutation ($id: Int!, $programOfficeId: Int, $levelOneApproverId: Int!) {
      eventCompleted(
        id: $id
        programOfficeId: $programOfficeId
        levelOneApproverId: $levelOneApproverId
      ) {
        statusCode
        message
      }
    }
  `
  const results = await client.mutate({
    mutation: EVENTINFO,
    variables: { id: eventId, programOfficeId, levelOneApproverId }
  })

  return results
}

export const allEvents = async (dateLimited?: string, userOnly?: boolean) => {
  const client = GraphqlClient()
  const EVENTINFO = gql`
    query ($dateLimited: String, $userOnly: Boolean) {
      events(dateLimited: $dateLimited, userOnly: $userOnly) {
        data {
          id
          title
          summary
          eventType
          status
          seriesId
          createrId
          creater {
            firstName
            lastName
          }
          levelOneApprover {
            id
            email
            name
          }
          roomBooking {
            id
            date
            startTime
            endTime
            category
          }
          programOffice {
            id
            title
          }
          otherApproverName
          otherApproverEmail
          eventCloseout {
            id
            numberOfRegistrants
            numberOfAttendees
            highlightsActionItems
            participantRatingsSummary
            files
          }
          publicationDate
          createdAt
          updatedAt
        }
        statusCode
        message
      }
    }
  `
  const results = await client.query({
    query: EVENTINFO,
    variables: { dateLimited, userOnly }
  })
  // return results;
  // loop through events and parse the files
  let eventsWithParsedFiles = null;
  if (results.data.events.data) {
    eventsWithParsedFiles = results.data.events.data.map((event: any) => {
      if (event.eventCloseout) {
        return {
          ...event,
          eventCloseout: {
            ...event.eventCloseout,
            files: JSON.parse(event.eventCloseout?.files || '[]')
          }
        }
      } else {
        return event;
      }
    })
  }
  return eventsWithParsedFiles ? {
    ...results,
    data: {
      ...results.data,
      events: {
        ...results.data.events,
        data: eventsWithParsedFiles
      }
    }
  } : results;
}

export const eventDetail = async (eventId: number) => {
  const client = GraphqlClient()
  const staffQuery = gql`
    query ($id: Int!) {
      eventDetail(id: $id) {
        data {
          id
          title
          summary
          description
          overHundredParticipants
          sendInvitation
          invitationDistributionList
          invitationNotes
          programOfficeId
          programOffice {
            id
            title
          }
          levelOneApproverId
          levelOneApprover {
            id
            name
            email
          }
          otherApproverName
          otherApproverEmail
          publicOrPrivate
          featuresTranscription
          featuresScript
          logisticNeed
          supportNeeded
          additionalRequest
          tbd_presenters
          tbd_moderators
          tbd_supportstaff
          status
          creater {
            id
            firstName
            lastName
            email
          }
          staff {
            id
            name
            email
            phone
            jobTitle
            programOffice
            staff_type
          }
          roomBooking {
            id
            roomId
            date
            startTime
            endTime
            zoomLink
            category
          }
          eventType
          seriesId
          publicationDate
          createdAt
          updatedAt
          latestApprovers {
            approver1
            approver2
            approver3
          }
        }
        statusCode
        message
      }
    }
  `
  const results = await client.query({
    query: staffQuery,
    variables: { id: eventId }
  })
  return results
}

export const eventWithoutDate = async () => {
  const client = GraphqlClient()
  const staffQuery = gql`
    query {
      eventWithoutDate {
        data {
          id
          title
          summary
          description
          overHundredParticipants
          sendInvitation
          invitationDistributionList
          invitationNotes
          publicOrPrivate
          logisticNeed
          supportNeeded
          additionalRequest
          tbd_presenters
          tbd_moderators
          tbd_supportstaff
          eventType
        }
        statusCode
        message
      }
    }
  `
  const results = await client.query({
    query: staffQuery
  })
  return results
}

export const getEventQueue = async () => {
  const client = GraphqlClient()
  const query = gql`
    query {
      eventQueue {
        data {
          id
          title
          status
          summary
          eventType
          seriesId
          createrId
          levelOneApproverEmail
          roomBooking {
            date
            startTime
            endTime
            category
          }
        }
        statusCode
        message
      }
    }
  `

  const results = await client.query({ query })
  return results
}

export const getBooking = async (eventId: number) => {
  const client = GraphqlClient()
  const staffQuery = gql`
    query ($eventId: Int!) {
      booking(eventId: $eventId) {
        data {
          id
          date
          startTime
          endTime
          category
        }
        statusCode
        message
      }
    }
  `
  const results = await client.query({
    query: staffQuery,
    variables: { eventId: eventId }
  })
  return results
}

export const userDetail = async (userId: number) => {
  const client = GraphqlClient()
  const staffQuery = gql`
    query ($id: Int!) {
      userDetail(id: $id) {
        data {
          id
          title
          summary
        }
        statusCode
        message
      }
    }
  `
  const results = await client.query({
    query: staffQuery,
    variables: { id: userId }
  })
  return results
}

export const getLevelOneApprovers = async () => {
  const client = GraphqlClient()

  const query = gql`
    query {
      levelOneApprovers {
        id
        firstName
        lastName
      }
    }
  `

  const results = await client.query({ query })
  return results
}

export const programOffice = async () => {
  const client = GraphqlClient()
  const programOfficeQuery = gql`
    query {
      programOffice {
        data {
          id
          title
          acronym
          levelOneApprover {
            id
            name
            email
            isDefault
          }
        }
        statusCode
        message
      }
    }
  `
  const results = await client.query({
    query: programOfficeQuery
  })
  return results
}

export const createSeries = async (eventId: number) => {
  const client = GraphqlClient()

  const EVENTINFO = gql`
    mutation ($id: Int!) {
      createSeries(id: $id) {
        data {
          id
          seriesId
        }
        statusCode
        message
      }
    }
  `
  const results = await client.mutate({
    mutation: EVENTINFO,
    variables: { id: eventId }
  })

  return results
}

export const seriesEvents = async (eventId: number) => {
  const client = GraphqlClient()
  const staffQuery = gql`
    query ($id: Int!) {
      seriesEvents(id: $id) {
        data {
          id
          title
          summary
          description
          status
          eventType
          createrId
          creater {
            firstName
            lastName
          }
          roomBooking {
            id
            date
            startTime
            endTime
            category
          }
          staff {
            id
            event_id
            name
            email
            staff_type
          }
          otherApproverName
          otherApproverEmail
          publicationDate
          createdAt
          updatedAt
        }
        statusCode
        message
      }
    }
  `
  const results = await client.query({
    query: staffQuery,
    variables: { id: eventId }
  })
  return results
}

export const allEventsBasedOnRole = async (
  eventType?: string,
  search?: string,
  page?: number,
  limit?: number,
  orderByField?: string,
  sortOrder?: string
) => {
  const client = GraphqlClient()
  const EVENTINFO = gql`
    query ($eventType: String, $search: String, $page: Int, $limit: Int, $orderByField: String, $sortOrder: String) {
      eventsBasedOnRole(
        eventType: $eventType
        search: $search
        page: $page
        limit: $limit
        orderByField: $orderByField
        sortOrder: $sortOrder
      ) {
        data {
          id
          title
          status
          createrId
          eventType
          creater {
            firstName
            lastName
          }
          roomBooking {
            id
            date
            startTime
            endTime
            category
          }
          otherApproverName
          otherApproverEmail
          eventCloseout {
            id
            numberOfRegistrants
            numberOfAttendees
            highlightsActionItems
            participantRatingsSummary
            files
          }
          createdAt
          updatedAt
        }
        statusCode
        message
      }
    }
  `
  const results = await client.query({
    query: EVENTINFO,
    variables: { eventType, search, page, limit, orderByField, sortOrder }
  })
  return results
}

export const getEventsCount = async (eventType?: string, search?: string) => {
  const client = GraphqlClient()

  const query = gql`
    query ($eventType: String, $search: String) {
      eventsCount(eventType: $eventType, search: $search)
    }
  `

  return await client.query({ query, variables: { eventType, search } })
}

export const createSeriesEvent = async (
  title: string,
  summary: string,
  eventType: string,
  seriesId: number
) => {
  const client = GraphqlClient()
  const EVENTINFO = gql`
    mutation (
      $title: String!
      $summary: String!
      $eventType: String!
      $seriesId: Int!
    ) {
      createSeriesEvent(
        title: $title
        summary: $summary
        eventType: $eventType
        seriesId: $seriesId
      ) {
        data {
          id
          title
          summary
          description
        }
        statusCode
        message
      }
    }
  `
  const results = await client.mutate({
    mutation: EVENTINFO,
    variables: {
      title: title,
      summary: summary,
      eventType: eventType,
      seriesId: seriesId
    }
  })
  return results
}
export const deleteSeriesEvent = async (id: number) => {
  const client = GraphqlClient()
  const deleteEventMutation = gql`
    mutation ($id: Int!) {
      deleteSeriesEvent(id: $id) {
        statusCode
        message
      }
    }
  `
  const results = await client.mutate({
    mutation: deleteEventMutation,
    variables: { id: id }
  })

  return results
}

export const deleteSeries = async (id: number) => {
  const client = GraphqlClient()

  const mutation = gql`
    mutation ($id: Int!) {
      deleteSeries(id: $id) {
        statusCode
        message
      }
    }
  `

  return await client.mutate({ mutation, variables: { id } })
}

export const updateEventSeries = async (id: number, seriesId: number) => {
	const client = GraphqlClient();
	const EVENTINFO = gql`
		mutation ($id: Int!, $seriesId: Int!) {
			updateEventSeries (id: $id, seriesId: $seriesId){
				data {
					id
          seriesId
				}
				statusCode
				message
			}
		}
	`;
	const results = await client.mutate({
		mutation: EVENTINFO,
		variables: { "id": id, "seriesId": seriesId},
	});
	return results;
};

export const allProgramOffice = async (page: number) => {
  const client = GraphqlClient()
  const query = gql`
    query ($page: Int!) {
      allProgramOffices(page: $page) {
        data {
          id
          title
          status
        }
        statusCode
        message
      }
    }
  `

  const results = await client.query({ query, variables: { page } })
  return results
}

export const getProgramOfficesCount = async () => {
  const client = GraphqlClient()

  const query = gql`
    query {
      programOfficesCount
    }
  `

  return await client.query({ query })
}

export const updateProgramOffice = async (
  id: number,
  title: string,
  acronym: string,
  status: ProgramOfficeStatus,
  levelOneApprover: LevelOneApprover[]
) => {
  const client = GraphqlClient()

  const mutation = gql`
    mutation ($id: Int!, $title: String!, $acronym: String!, $status: String!, $levelOneApprover: [LevelOneApproverInput]!) {
      updateProgramOffice(id: $id, title: $title, acronym: $acronym, status: $status, levelOneApprover: $levelOneApprover) {
        statusCode
        message
      }
    }
  `

  const results = await client.mutate({
    mutation,
    variables: { id, title, acronym, status, levelOneApprover }
  })
  return results
}

export const updateProgramOfficeStatus = async (
  id: number,
  status: ProgramOfficeStatus
) => {
  const client = GraphqlClient()

  const mutation = gql`
    mutation ($id: Int!, $status: String!) {
      updateProgramOfficeStatus(id: $id, status: $status) {
        statusCode
        message
      }
    }
  `
  const results = await client.mutate({
    mutation,
    variables: { id, status }
  })

  return results
}

export const createEventCloseout = async (
  eventId: number,
  numberOfRegistrants: number,
  numberOfAttendees: number,
  highlightsActionItems: string,
  participantRatingsSummary: string,
  files: any
) => {
  const client = FileUploadGraphqlClient()

  const mutation = gql`
    mutation (
      $eventId: Int!
      $numberOfRegistrants: Int
      $numberOfAttendees: Int
      $highlightsActionItems: String
      $participantRatingsSummary: String
      $files: [Upload]!
    ) {
      createEventCloseout(
        eventId: $eventId
        numberOfRegistrants: $numberOfRegistrants
        numberOfAttendees: $numberOfAttendees
        highlightsActionItems: $highlightsActionItems
        participantRatingsSummary: $participantRatingsSummary
        files: $files
      ) {
        data {
          id
          eventId
          numberOfRegistrants
          numberOfAttendees
          highlightsActionItems
          participantRatingsSummary
          files
        }
        statusCode
        message
      }
    }
  `

  const results = await client.mutate({
    mutation,
    variables: {
      eventId,
      numberOfRegistrants,
      numberOfAttendees,
      highlightsActionItems,
      participantRatingsSummary,
      files
    }
  })

  return results
}

export const updateEventCloseout = async (
  id: number,
  numberOfRegistrants: number,
  numberOfAttendees: number,
  highlightsActionItems: string,
  participantRatingsSummary: string,
  files: string,
  newFiles: any
) => {
  const client = FileUploadGraphqlClient()

  const mutation = gql`
    mutation (
      $id: Int!
      $numberOfRegistrants: Int
      $numberOfAttendees: Int
      $highlightsActionItems: String
      $participantRatingsSummary: String
      $files: String
      $newFiles: [Upload]!
    ) {
      updateEventCloseout(
        id: $id
        numberOfRegistrants: $numberOfRegistrants
        numberOfAttendees: $numberOfAttendees
        highlightsActionItems: $highlightsActionItems
        participantRatingsSummary: $participantRatingsSummary
        files: $files
        newFiles: $newFiles
      ) {
        data {
          id
          eventId
          numberOfRegistrants
          numberOfAttendees
          highlightsActionItems
          participantRatingsSummary
          files
        }
        statusCode
        message
      }
    }
  `

  const results = await client.mutate({
    mutation,
    variables: {
      id,
      numberOfRegistrants,
      numberOfAttendees,
      highlightsActionItems,
      participantRatingsSummary,
      files,
      newFiles
    }
  })

  return results
}

export const unlockEvent = async (id: number) => {
  const client = GraphqlClient()

  const mutation = gql`
    mutation ($id: Int!) {
      unlockEvent(id: $id) {
        statusCode
        message
      }
    }
  `

  const results = await client.mutate({
    mutation,
    variables: { id }
  })

  return results
}

export const transferEventRequester = async (id: number, newRequesterId: number) => {
  const client = GraphqlClient()

  const mutation = gql`
    mutation ($id: Int!, $newRequesterId: Int!) {
      transferEventRequester(id: $id, newRequesterId: $newRequesterId) {
        statusCode
        message
      }
    }
  `

  const results = await client.mutate({
    mutation,
    variables: { id, newRequesterId }
  })

  return results
}

export const approveEventService = async (id: number) => {
	const client = GraphqlClient();
	const mutation = gql`
		mutation ($id: Int!) {
			approveEvent (id: $id){
				statusCode
				message
			}
		}
	`;
	const results = await client.mutate({
		mutation,
		variables: { id },
	});
	return results;
};

